import { writable } from "svelte/store"

export const DEFAULT_TRANSITION_DURATION = 700

export type FlowOptions = {
  /** Delay before stage switch. Only for forward stages */
  transitionDuration?: number
}

export const createFlow = <T>(stages: readonly T[], options: FlowOptions = {}) => {
  let stage: T | null = null

  const { subscribe, update } = writable<{
    stage: T | null
    running: boolean
    backward: boolean
  }>({ stage, running: false, backward: false })

  let timer: ReturnType<typeof setTimeout> | null = null
  let next: T | null

  const go = (to: T | null, overrideOptions?: FlowOptions) => {
    next = to

    if (timer) return

    const transitionDuration =
      overrideOptions?.transitionDuration ??
      options.transitionDuration ??
      DEFAULT_TRANSITION_DURATION
    const transition = transitionDuration > 0 && isForward(stage, next, stages)

    update((state) => ({
      ...state,
      running: transition,
      backward: isBackward(stage, next, stages),
    }))

    if (!transition) {
      update((state) => ({ ...state, stage: (stage = next) }))

      return
    }

    timer = setTimeout(() => {
      timer = null
      update((state) => ({
        ...state,
        stage: (stage = next),
        running: false,
      }))
    }, transitionDuration)
  }

  return { subscribe, go }
}

const isForward = <T>(from: T, to: T, order: readonly T[]) =>
  Boolean(from && to) && order.indexOf(to) > order.indexOf(from)

const isBackward = <T>(from: T, to: T, order: readonly T[]) =>
  Boolean(from && to) && order.indexOf(to) < order.indexOf(from)
